import { useNavigate, useParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';

import { GroupMemberMigration } from '../../api/migrationGroups';
import BprtStatusBadge from '../../components/BprtStatusBadge';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import RelativeTime from '../../components/RelativeTime';
import TenantLabel from '../../components/TenantLabel';
import { useGroupDetailQuery } from '../../hooks/useGroupDetail';

const useStyles = makeStyles({
  tableSurface: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
  muted: { color: '#a1a1aa' },
});

// The member table renders the SAME columns as the migrations list — name,
// wave, type, tenants, status, devices. This is where the Tenants tab's biggest
// gap gets fixed rather than repeated: `/tenants/:id/migrations` shows raw
// GUIDs, which tells an operator nothing about what they are looking at.
export default function GroupMigrationsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const query = useGroupDetailQuery(groupId);

  if (!query.data) return null;
  const migrations = query.data.migrations;

  const columns: DataTableColumn<GroupMemberMigration>[] = [
    {
      key: 'name',
      header: 'Migration',
      render: (m) => <span className="font-medium">{m.migrationName}</span>,
    },
    { key: 'wave', header: 'Wave', render: (m) => m.migrationWave },
    { key: 'type', header: 'Type', render: (m) => m.migrationType || '—' },
    {
      key: 'destination',
      header: 'Destination',
      render: (m) =>
        m.destTenantId || m.destTenantDomain ? (
          <TenantLabel tenantId={m.destTenantId} domain={m.destTenantDomain} />
        ) : (
          // A D2C migration with no explicit destination targets the deployment
          // tenant (FD-ADR-029). Saying so beats an em-dash that reads as
          // missing data.
          <span className={styles.muted}>Deployment tenant</span>
        ),
    },
    { key: 'bprt', header: 'Wave status', render: (m) => <BprtStatusBadge status={m.bprtStatus} /> },
    {
      key: 'devices',
      header: 'Devices assigned',
      numeric: true,
      align: 'right',
      render: (m) => m.deviceCount,
    },
    { key: 'created', header: 'Created', render: (m) => <RelativeTime iso={m.createdAt} /> },
  ];

  return (
    <div className={styles.tableSurface}>
      <DataTable
        columns={columns}
        data={migrations}
        keyForRow={(m) => m.migrationId}
        onRowClick={(m) => navigate(`/migrations/${encodeURIComponent(m.migrationId)}/overview`)}
        emptyState={
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>No migrations in this group</MessageBarTitle>
              Open a migration and set its group from the migration header, or use “Add migrations”
              here.
            </MessageBarBody>
          </MessageBar>
        }
      />
    </div>
  );
}
